import { Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ConfirmDialog } from "../../components/settings/confirm-dialog";
import { type Valuer, ValuerForm } from "../../components/settings/valuer-form";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { useValuers } from "../../hooks/use-api";
import { useCreateValuer, useDeleteValuer, useUpdateValuer } from "../../hooks/use-settings";

export function ValuersPage(): React.ReactElement {
  const { data, isLoading, error } = useValuers();
  const createMutation = useCreateValuer();
  const updateMutation = useUpdateValuer();
  const deleteMutation = useDeleteValuer();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const valuers = data?.items ?? [];
  const editing = valuers.find((v) => v.id === editingId);

  const handleSave = (v: Partial<Valuer>): void => {
    if (v.id) {
      updateMutation.mutate(
        {
          id: v.id,
          name: v.name,
          shortCode: v.shortCode,
          qualifications: v.qualifications ?? undefined,
          email: v.email ?? undefined,
          isActive: v.isActive,
        },
        {
          onSuccess: () => {
            toast.success("Valuer updated");
            setShowForm(false);
          },
          onError: () => toast.error("Failed to update valuer"),
        },
      );
    } else {
      createMutation.mutate(
        {
          name: v.name ?? "",
          shortCode: v.shortCode ?? "",
          qualifications: v.qualifications || undefined,
          email: v.email || undefined,
          isActive: v.isActive,
        },
        {
          onSuccess: () => {
            toast.success("Valuer created");
            setShowForm(false);
          },
          onError: () => toast.error("Failed to create valuer"),
        },
      );
    }
  };

  const handleConfirmDelete = (): void => {
    if (pendingDeleteId === null) return;
    deleteMutation.mutate(pendingDeleteId, {
      onSuccess: () => toast.success("Valuer deactivated"),
      onError: () => toast.error("Failed to deactivate valuer"),
    });
    setPendingDeleteId(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div>
          <h2 className="text-lg font-semibold">Valuers</h2>
          <p className="text-sm text-muted-foreground">Manage valuers, qualifications, and signature images.</p>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setShowForm(true);
            setEditingId(null);
          }}
          data-testid="add-valuer"
        >
          <Plus className="h-3.5 w-3.5" /> Add New
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground" data-testid="valuers-loading">
          Loading…
        </p>
      ) : error ? (
        <p className="text-sm text-destructive" data-testid="valuers-error">
          Failed to load valuers.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="w-20">Code</TableHead>
              <TableHead>Qualifications</TableHead>
              <TableHead className="w-24">Status</TableHead>
              <TableHead className="w-24">Signature</TableHead>
              <TableHead className="w-24" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {valuers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  No valuers configured yet.
                </TableCell>
              </TableRow>
            ) : (
              valuers.map((v) => (
                <TableRow key={v.id}>
                  <TableCell className="font-medium">{v.name}</TableCell>
                  <TableCell className="text-muted-foreground">{v.shortCode}</TableCell>
                  <TableCell className="text-sm">{v.qualifications}</TableCell>
                  <TableCell>
                    <Badge variant={v.isActive ? "default" : "secondary"}>{v.isActive ? "Active" : "Inactive"}</Badge>
                  </TableCell>
                  <TableCell>
                    {v.signatureUrl ? (
                      <span className="text-xs text-primary">Uploaded</span>
                    ) : (
                      <span className="text-xs text-muted-foreground">None</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingId(v.id);
                          setShowForm(true);
                        }}
                        data-testid={`edit-valuer-${v.id}`}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setPendingDeleteId(v.id)}
                        data-testid={`delete-valuer-${v.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Valuer" : "Add New Valuer"}</DialogTitle>
          </DialogHeader>
          <ValuerForm initial={editing ?? {}} onSave={handleSave} onCancel={() => setShowForm(false)} />
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={pendingDeleteId !== null}
        title="Deactivate Valuer"
        message="This valuer will be hidden from new records but preserved on existing ones. Continue?"
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDeleteId(null)}
      />
    </div>
  );
}
